import { c as _c } from "react/compiler-runtime";
import useRealEstateDetail from "@/hooks/QueryHooks/useRealEstateDetail";
import { useRef, useState } from "react";
import ImageLightbox from "./components/ImagesFeature/image-lightbox/ImageLightbox";
import { ShareDrawer } from "./components/share-drawer";
import { LocationCard } from "@/components/molecules/LocationCard/LocationCard";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useDialogDetails } from "../dialogComponents/DialogProvider";
import { useView } from "../../../pages/search/providers/ViewProvider";
import CardFavorite from "@/components/cardElements/FavoritesModalGroup/CardFavorite";
import { cn, formatLocationDisplay } from "@/lib/utils";
import { mergeListingImages } from "@/utils/listingImages";
import { isSearchRoute } from "@/utils/urlManipulation";
import { HeroGridCard } from "@/components/molecules/HeroGridCard/HeroGridCard";
import { EleiloesSubType } from "@/types/real-estate-features";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { RealEstateAttributes } from "./components/real-estate-attributes/RealEstateAttributes";
import { Badge } from "@/components/ui/badge";
import Price from "@/components/atoms/Price";
import Compare from "./components/compare/Compare";
import { getPriceDisplay } from "@/lib/price";
import RealEstateAgencyCard from "../components/RealEstateAgencyCard/RealEstateAgencyCard";
import { DemandUserCard } from "./components/demand-user-card/DemandUserCard";
import { ListingType } from "../../listings/api/schema";
import { useBoundaries } from "@/hooks/useBoundaries";
import { convertPolygon } from "@/components/pages/search/map/components/MapPolygons";
import { selectDemandBoundaryNodes } from "../demandLocation";
import { DemandLocationName } from "../demand-location-name/DemandLocationName";
import { useLocale, useTranslations } from "next-intl";
import { formatTimeAgo } from "@/lib/time";
import { useFiltersContext } from "@/components/providers/FiltersProvider/FiltersProvider";
import { ListingNotFound } from "@/components/modules/listings/ListingDetail/listing-not-found/ListingNotFound";
import { useMapContext } from "../../../organisms/map/components/MapProvider";
import { BusinessType } from "@/types/real-estate";
import RealEstateDescriptionText, { hasRenderableDescription } from "./components/RealEstateDescriptionText";
import RealEstateFeaturesCards from "./components/RealEstateFeaturesCards";
import { RoomFeaturePills } from "./components/RoomFeaturePills";
import { getListingFeaturePills } from "@/components/modules/listings/mappers";
import { AreaContent } from "@/components/molecules/Area/AreaContent";
import { RealEstateDetailHouseRules } from "./components/RealEstateDetailHouseRules";
import { RealEstateDetailBills } from "./components/RealEstateDetailBills";
import { RealEstateDetailLeftBadges } from "./components/RealEstateDetailLeftBadges";
import { RoomRail, type RoomRailItem } from "./components/room-rail/RoomRail";
import { useRoomReason } from "./components/room-rail/useRoomRail";
import { SearchTypes } from "@/constants/filters";
import { buildRoomFilterParams } from "../roomFilterParams";
import NotificationMessage from "@/components/atoms/notifications/NotificationMessage";
type Props = {
  realEstateId: string | undefined;
  inDialog?: boolean;
  onRoomChange?: (slug: string) => void;
};
export default function RealEstateDialogContent(t0) {
  const $ = _c(199);
  const {
    realEstateId,
    inDialog,
    onRoomChange
  } = t0;
  const pathname = usePathname();
  const router = useRouter();
  const {
    handleGoToSearch,
    params: filterParams,
    businessType: searchBusinessType
  } = useFiltersContext();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== filterParams || $[1] !== searchBusinessType) {
    t1 = searchBusinessType === SearchTypes.RENT_ROOM ? buildRoomFilterParams(filterParams) : undefined;
    $[0] = filterParams;
    $[1] = searchBusinessType;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const detailFilters = t1;
  let t2;
  if ($[3] !== detailFilters || $[4] !== realEstateId) {
    t2 = {
      realEstateSlug: realEstateId,
      filters: detailFilters
    };
    $[3] = detailFilters;
    $[4] = realEstateId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const realEstateDetail = useRealEstateDetail(t2);
  const roomReason = useRoomReason(detailFilters);
  const tRealEstateDetail = useTranslations("modules.realEstateDetail");
  const locale = useLocale();
  const detailData = realEstateDetail.data?.data?.realEstate;
  const analytics = realEstateDetail.data?.data?.analytics;
  const locationData = realEstateDetail.data?.data?.locations;
  const featuresData = realEstateDetail.data?.data?.features;
  let t3;
  if ($[6] !== featuresData) {
    t3 = getListingFeaturePills(featuresData);
    $[6] = featuresData;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const featurePills = t3;
  const relatedListings = realEstateDetail.data?.data?.relatedListings ?? [];
  let t4;
  if ($[8] !== pathname) {
    t4 = isSearchRoute(pathname);
    $[8] = pathname;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const isInSearch = t4;
  const isFavorite = realEstateDetail.data?.data?.isFavorite;
  const {
    setForcedMapTarget
  } = useMapContext();
  const {
    handleCloseDialog,
    handleOpenDialog
  } = useDialogDetails();
  const {
    handleShowMap,
    mapSize,
    showMap
  } = useView();
  const isRoomHouse = detailData?.businessType === BusinessType.RoomRent && relatedListings.length > 0 && !!detailData?.slug;
  const roomRailItems = isRoomHouse && detailData ? [...relatedListings.map(_temp), {
    slug: detailData.slug,
    title: detailData.title,
    rentPrice: detailData.rentPrice,
    thumbnail: detailData.thumbnails?.[0] ?? detailData.pictures?.[0] ?? null,
    matchesFilters: realEstateDetail.data?.data?.matchesFilters,
    gender: detailData.roomData?.gender ?? null,
    availableFrom: detailData.roomData?.availableFrom ?? null,
    petFriendly: detailData.roomData?.petFriendly ?? null,
    contract: detailData.roomData?.contract ?? null,
    smokingAllowed: detailData.roomData?.smokingAllowed ?? null
  }].sort(_temp2) : [];
  let t5;
  if ($[10] !== detailData?.slug || $[11] !== handleOpenDialog || $[12] !== inDialog || $[13] !== onRoomChange || $[14] !== pathname || $[15] !== router || $[16] !== searchParams) {
    t5 = slug => {
      if (slug === detailData?.slug) {
        return;
      }
      if (onRoomChange) {
        onRoomChange(slug);
        return;
      }
      if (inDialog) {
        handleOpenDialog(slug);
        return;
      }
      const parts = pathname.split("/");
      parts[parts.length - 1] = slug;
      const query = searchParams?.toString();
      router.push(query ? `${parts.join("/")}?${query}` : parts.join("/"));
    };
    $[10] = detailData?.slug;
    $[11] = handleOpenDialog;
    $[12] = inDialog;
    $[13] = onRoomChange;
    $[14] = pathname;
    $[15] = router;
    $[16] = searchParams;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const handleRoomSelect = t5;
  const favRef = useRef(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const realEstateType = EleiloesSubType[detailData?.realEstateType ?? featuresData?.find(_temp3)?.featureValue];
  featuresData?.find(_temp4);
  featuresData?.find(_temp5);
  let t6;
  if ($[18] !== locationData) {
    t6 = formatLocationDisplay(locationData);
    $[18] = locationData;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const locationDisplay = t6;
  const user = realEstateDetail.data?.data?.user;
  const isDemand = detailData?.listingType === ListingType.DEMAND;
  const t7 = detailData?.pictures;
  const t8 = detailData?.portfolioData?.imageUrls;
  let t9;
  if ($[20] !== t7 || $[21] !== t8) {
    t9 = mergeListingImages(t7, t8);
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const pictures = t9;
  let t10;
  if ($[23] !== isDemand || $[24] !== locationData) {
    t10 = isDemand ? selectDemandBoundaryNodes(locationData) : [];
    $[23] = isDemand;
    $[24] = locationData;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const geometryByCode = useBoundaries(t10);
  let t11;
  if ($[26] !== geometryByCode) {
    t11 = [...geometryByCode.values()].map(_temp6).filter(_temp7);
    $[26] = geometryByCode;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const locationPolygons = t11;
  let t12;
  if ($[28] !== searchParams) {
    t12 = searchParams?.has("modal");
    $[28] = searchParams;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const isFullscreenDetail = !t12;
  const businessType = detailData?.businessType;
  const hasEnergyCert = !!detailData?.energyCertificationType;
  const hasRealEstateType = !!realEstateType;
  const hasAreaCell = !!detailData?.totalArea && businessType !== BusinessType.RoomRent;
  const hasPricePerSqmCell = businessType !== BusinessType.Rent && businessType !== BusinessType.RoomRent;
  const hasFeaturesListSection = Array.isArray(featuresData) && featuresData.length > 0 && businessType !== BusinessType.Auction;
  const hasFeatureGridContent = hasEnergyCert || hasRealEstateType || hasAreaCell || hasPricePerSqmCell || hasFeaturesListSection;
  const fillImageHeight = !isDemand && pictures.length > 0;
  let t13;
  if ($[30] !== detailData?.slug || $[31] !== detailFilters || $[32] !== handleRoomSelect || $[33] !== realEstateId || $[34] !== roomRailItems) {
    t13 = roomRailItems.length > 1 ? <RoomRail rooms={roomRailItems} activeSlug={realEstateId ?? detailData?.slug ?? ""} onSelect={handleRoomSelect} filterParams={detailFilters} /> : null;
    $[30] = detailData?.slug;
    $[31] = detailFilters;
    $[32] = handleRoomSelect;
    $[33] = realEstateId;
    $[34] = roomRailItems;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  const railNode = t13;
  let t14;
  if ($[36] !== detailData || $[37] !== handleCloseDialog || $[38] !== handleShowMap || $[39] !== mapSize || $[40] !== setForcedMapTarget || $[41] !== showMap) {
    t14 = () => {
      if (!detailData) {
        return;
      }
      if (mapSize === "max-size" && showMap) {
        handleCloseDialog(detailData.id);
      } else {
        if (!showMap) {
          handleShowMap();
        }
        handleCloseDialog(detailData.id);
        setForcedMapTarget({
          realEstateId: detailData.id
        });
      }
    };
    $[36] = detailData;
    $[37] = handleCloseDialog;
    $[38] = handleShowMap;
    $[39] = mapSize;
    $[40] = setForcedMapTarget;
    $[41] = showMap;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  const handleNavigateInMap = t14;
  let t15;
  if ($[43] !== handleGoToSearch || $[44] !== handleNavigateInMap || $[45] !== isFullscreenDetail || $[46] !== isInSearch) {
    t15 = () => {
      if (isFullscreenDetail || !isInSearch) {
        handleGoToSearch();
        setTimeout(() => {
          handleNavigateInMap();
        }, 1000);
      } else {
        handleNavigateInMap();
      }
    };
    $[43] = handleGoToSearch;
    $[44] = handleNavigateInMap;
    $[45] = isFullscreenDetail;
    $[46] = isInSearch;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  const handleGoToMap = t15;
  if (realEstateDetail.isError && !realEstateDetail.isLoading) {
    const t16 = !inDialog && "min-h-[90vh]";
    let t17;
    if ($[48] !== t16) {
      t17 = cn("py-4 flex items-center justify-center h-full", t16);
      $[48] = t16;
      $[49] = t17;
    } else {
      t17 = $[49];
    }
    let t18;
    if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <ListingNotFound />;
      $[50] = t18;
    } else {
      t18 = $[50];
    }
    let t19;
    if ($[51] !== t17) {
      t19 = <div className={t17}>{t18}</div>;
      $[51] = t17;
      $[52] = t19;
    } else {
      t19 = $[52];
    }
    return t19;
  }
  if (!detailData || realEstateDetail.isLoading) {
    const t16 = !inDialog && "min-h-[90vh]";
    let t17;
    if ($[53] !== t16) {
      t17 = cn("py-4 flex items-center justify-center h-full", t16);
      $[53] = t16;
      $[54] = t17;
    } else {
      t17 = $[54];
    }
    let t18;
    if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <div className="animate-spin h-8 w-8 border-4 border-border border-t-transparent rounded-full mx-auto mb-4" />;
      $[55] = t18;
    } else {
      t18 = $[55];
    }
    let t19;
    if ($[56] !== tRealEstateDetail) {
      t19 = tRealEstateDetail("loadingProperty");
      $[56] = tRealEstateDetail;
      $[57] = t19;
    } else {
      t19 = $[57];
    }
    let t20;
    if ($[58] !== t19) {
      t20 = <div className="text-center">{t18}<p className="text-muted-foreground">{t19}</p></div>;
      $[58] = t19;
      $[59] = t20;
    } else {
      t20 = $[59];
    }
    let t21;
    if ($[60] !== t17 || $[61] !== t20) {
      t21 = <div className={t17}>{t20}</div>;
      $[60] = t17;
      $[61] = t20;
      $[62] = t21;
    } else {
      t21 = $[62];
    }
    return t21;
  }
  let t16;
  if ($[63] !== detailData.rentPrice || $[64] !== detailData.roomData?.availableFrom || $[65] !== detailData.roomData?.contract || $[66] !== detailData.roomData?.gender || $[67] !== detailData.roomData?.petFriendly || $[68] !== detailData.roomData?.smokingAllowed || $[69] !== detailData.slug || $[70] !== detailData.title || $[71] !== isRoomHouse || $[72] !== realEstateDetail.data?.data?.matchesFilters || $[73] !== roomReason) {
    t16 = isRoomHouse && realEstateDetail.data?.data?.matchesFilters === false ? roomReason({
      slug: detailData.slug,
      title: detailData.title,
      rentPrice: detailData.rentPrice ?? null,
      gender: detailData.roomData?.gender ?? null,
      availableFrom: detailData.roomData?.availableFrom ?? null,
      petFriendly: detailData.roomData?.petFriendly ?? null,
      contract: detailData.roomData?.contract ?? null,
      smokingAllowed: detailData.roomData?.smokingAllowed ?? null
    }) : null;
    $[63] = detailData.rentPrice;
    $[64] = detailData.roomData?.availableFrom;
    $[65] = detailData.roomData?.contract;
    $[66] = detailData.roomData?.gender;
    $[67] = detailData.roomData?.petFriendly;
    $[68] = detailData.roomData?.smokingAllowed;
    $[69] = detailData.slug;
    $[70] = detailData.title;
    $[71] = isRoomHouse;
    $[72] = realEstateDetail.data?.data?.matchesFilters;
    $[73] = roomReason;
    $[74] = t16;
  } else {
    t16 = $[74];
  }
  const currentRoomOutsideReason = t16;
  let t17;
  if ($[75] !== currentRoomOutsideReason || $[76] !== tRealEstateDetail) {
    t17 = currentRoomOutsideReason ? <NotificationMessage variant="warning" hasCloseButton={false} title={tRealEstateDetail("roomOutsideFiltersTitle")} description={currentRoomOutsideReason} /> : null;
    $[75] = currentRoomOutsideReason;
    $[76] = tRealEstateDetail;
    $[77] = t17;
  } else {
    t17 = $[77];
  }
  const outsideNotification = t17;
  const t18 = !inDialog && "2xl:grid-cols-4";
  let t19;
  if ($[78] !== t18) {
    t19 = cn("grid grid-cols-1 lg:grid-cols-3 gap-4", t18);
    $[78] = t18;
    $[79] = t19;
  } else {
    t19 = $[79];
  }
  const t20 = !inDialog && "2xl:col-span-3";
  let t21;
  if ($[80] !== t20) {
    t21 = cn("flex flex-col gap-2 lg:col-span-2", t20);
    $[80] = t20;
    $[81] = t21;
  } else {
    t21 = $[81];
  }
  let t22;
  if ($[82] !== detailData.roomData?.gender || $[83] !== detailData.title || $[84] !== fillImageHeight || $[85] !== inDialog || $[86] !== isDemand || $[87] !== lightboxIndex || $[88] !== pictures || $[89] !== railNode || $[90] !== user) {
    t22 = <div className="relative">{isDemand ? <DemandUserCard user={user} gender={detailData?.roomData?.gender} /> : <><HeroGridCard bare={true} fill={fillImageHeight} title="" subtitle="" contentClassName="gap-1" variant="compact" pictures={pictures} progressive={true} altPrefix={`Property image ${detailData?.title}`} imagePriority={true} imageQuality={90} imageSizes={{
          primary: "(max-width: 768px) 100vw, 60vw",
          secondary: "(max-width: 768px) 100vw, 25vw"
        }} onTileClick={() => setLightboxIndex(0)} morePhotosLabel={_temp8}>{inDialog && railNode}</HeroGridCard><ImageLightbox pictures={pictures} open={lightboxIndex !== null} initialIndex={lightboxIndex ?? 0} onClose={() => setLightboxIndex(null)} /></>}</div>;
    $[82] = detailData.roomData?.gender;
    $[83] = detailData.title;
    $[84] = fillImageHeight;
    $[85] = inDialog;
    $[86] = isDemand;
    $[87] = lightboxIndex;
    $[88] = pictures;
    $[89] = railNode;
    $[90] = user;
    $[91] = t22;
  } else {
    t22 = $[91];
  }
  const t23 = inDialog && outsideNotification;
  let t24;
  if ($[92] !== analytics?.favoritesCount || $[93] !== analytics?.viewCount || $[94] !== detailData.bathrooms || $[95] !== detailData.bedrooms || $[96] !== detailData.businessType || $[97] !== detailData.id || $[98] !== detailData.lastBidPrice || $[99] !== detailData.minPrice || $[100] !== detailData.rentPrice || $[101] !== detailData.roomData?.maxPersons || $[102] !== detailData.salePrice || $[103] !== detailData.slug || $[104] !== detailData.title || $[105] !== inDialog || $[106] !== isFavorite || $[107] !== realEstateId || $[108] !== tRealEstateDetail) {
    t24 = !inDialog && <div className="flex justify-between items-center gap-4"><RealEstateDetailLeftBadges realEstateNumRooms={detailData?.bedrooms} realEstateBathrooms={detailData?.bathrooms} numPersonsAllowed={detailData?.roomData?.maxPersons} /><div className="flex gap-2 items-center shrink-0"><Badge className="rounded-full bg-card hover:bg-card" variant="outline" size="md" onClick={e => {
          favRef.current?.click();
        }}><CardFavorite ref={favRef} fill="white" className={cn("w-4 h-4 flex gap-2 items-center cursor-pointer", isFavorite ? "text-red-500" : "text-foreground ")} realEstateSlug={detailData?.slug} realEstateId={detailData?.id} favorite={isFavorite} /><span>{analytics?.favoritesCount}</span></Badge><Badge className="rounded-full bg-card hover:bg-card" variant="outline" size="md"><ShareDrawer realEstateId={realEstateId} className={cn("w-4 h-4 flex gap-2 items-center cursor-pointer hover:underline text-foreground")} fill="white" title={detailData?.title}><span className="text-foreground">{tRealEstateDetail("share")}</span></ShareDrawer></Badge><Badge className="rounded-full cursor-default bg-card hover:bg-card" variant="outline" size="md">{tRealEstateDetail("views")}{" "}<span>{analytics?.viewCount}</span></Badge><Price businessType={detailData.businessType} minPrice={detailData.minPrice} lastBidPrice={detailData.lastBidPrice} salePrice={detailData.salePrice} rentPrice={detailData.rentPrice} className="text-2xl! font-bold shrink-0 text-foreground-strong ml-1" /></div></div>;
    $[92] = analytics?.favoritesCount;
    $[93] = analytics?.viewCount;
    $[94] = detailData.bathrooms;
    $[95] = detailData.bedrooms;
    $[96] = detailData.businessType;
    $[97] = detailData.id;
    $[98] = detailData.lastBidPrice;
    $[99] = detailData.minPrice;
    $[100] = detailData.rentPrice;
    $[101] = detailData.roomData?.maxPersons;
    $[102] = detailData.salePrice;
    $[103] = detailData.slug;
    $[104] = detailData.title;
    $[105] = inDialog;
    $[106] = isFavorite;
    $[107] = realEstateId;
    $[108] = tRealEstateDetail;
    $[109] = t24;
  } else {
    t24 = $[109];
  }
  const t25 = !inDialog && railNode;
  const t26 = !inDialog && outsideNotification;
  const t27 = !inDialog || isRoomHouse;
  let t28;
  if ($[110] !== detailData || $[111] !== realEstateType || $[112] !== t27) {
    t28 = <RealEstateAttributes detailData={detailData} realEstateType={realEstateType} bordered={t27} data-sentry-element="RealEstateAttributes" data-sentry-source-file="RealEstateDetail.tsx" />;
    $[110] = detailData;
    $[111] = realEstateType;
    $[112] = t27;
    $[113] = t28;
  } else {
    t28 = $[113];
  }
  let t29;
  if ($[114] !== detailData.description || $[115] !== hasFeatureGridContent || $[116] !== inDialog || $[117] !== tRealEstateDetail) {
    t29 = hasRenderableDescription(detailData.description) && <div className="mt-6"><h2 className="text-lg font-bold mb-4 text-foreground-heading">{tRealEstateDetail("description")}</h2><RealEstateDescriptionText description={detailData.description} compact={inDialog} hideExpand={!hasFeatureGridContent} /></div>;
    $[114] = detailData.description;
    $[115] = hasFeatureGridContent;
    $[116] = inDialog;
    $[117] = tRealEstateDetail;
    $[118] = t29;
  } else {
    t29 = $[118];
  }
  let t30;
  if ($[119] !== detailData.businessType || $[120] !== detailData.description || $[121] !== featurePills || $[122] !== featuresData) {
    t30 = featuresData && detailData.businessType !== BusinessType.Auction && Array.isArray(featuresData) && featuresData.length > 0 && <div className="mt-10">{hasRenderableDescription(detailData.description) && <Separator className="mb-6" />}{featurePills.hasAny ? <RoomFeaturePills room={featurePills.room} house={featurePills.house} /> : <RealEstateFeaturesCards features={featuresData} />}</div>;
    $[119] = detailData.businessType;
    $[120] = detailData.description;
    $[121] = featurePills;
    $[122] = featuresData;
    $[123] = t30;
  } else {
    t30 = $[123];
  }
  let t31;
  if ($[124] !== t24 || $[125] !== t25 || $[126] !== t26 || $[127] !== t28 || $[128] !== t29 || $[129] !== t30) {
    t31 = <div className="flex flex-col gap-2">{t24}{t25}{t26}{t28}{t29}{t30}</div>;
    $[124] = t24;
    $[125] = t25;
    $[126] = t26;
    $[127] = t28;
    $[128] = t29;
    $[129] = t30;
    $[130] = t31;
  } else {
    t31 = $[130];
  }
  let t32;
  if ($[131] !== t21 || $[132] !== t22 || $[133] !== t23 || $[134] !== t31) {
    t32 = <div className={t21}>{t22}{t23}{t31}</div>;
    $[131] = t21;
    $[132] = t22;
    $[133] = t23;
    $[134] = t31;
    $[135] = t32;
  } else {
    t32 = $[135];
  }
  const t33 = !inDialog && "sticky top-12";
  let t34;
  if ($[136] !== t33) {
    t34 = cn("flex flex-col gap-2", t33);
    $[136] = t33;
    $[137] = t34;
  } else {
    t34 = $[137];
  }
  let t35;
  if ($[138] !== tRealEstateDetail) {
    t35 = tRealEstateDetail("location");
    $[138] = tRealEstateDetail;
    $[139] = t35;
  } else {
    t35 = $[139];
  }
  let t36;
  if ($[140] !== isDemand || $[141] !== locationData || $[142] !== locationDisplay) {
    t36 = isDemand ? <DemandLocationName locations={locationData} /> : locationDisplay;
    $[140] = isDemand;
    $[141] = locationData;
    $[142] = locationDisplay;
    $[143] = t36;
  } else {
    t36 = $[143];
  }
  const t37 = isDemand ? undefined : detailData?.lat;
  const t38 = isDemand ? undefined : detailData?.lng;
  const t39 = isDemand ? locationPolygons : undefined;
  let t40;
  if ($[144] !== handleGoToMap || $[145] !== t35 || $[146] !== t36 || $[147] !== t37 || $[148] !== t38 || $[149] !== t39) {
    t40 = <LocationCard title={t35} locationName={t36} lat={t37} lng={t38} polygon={t39} onMapClick={handleGoToMap} mapClassName="h-56" data-sentry-element="LocationCard" data-sentry-source-file="RealEstateDetail.tsx" />;
    $[144] = handleGoToMap;
    $[145] = t35;
    $[146] = t36;
    $[147] = t37;
    $[148] = t38;
    $[149] = t39;
    $[150] = t40;
  } else {
    t40 = $[150];
  }
  const t41 = detailData?.title;
  const t42 = realEstateDetail.data?.data;
  let t43;
  if ($[151] !== realEstateId || $[152] !== t41 || $[153] !== t42) {
    t43 = <RealEstateAgencyCard title={t41} realEstateId={realEstateId} realEstateData={t42} data-sentry-element="RealEstateAgencyCard" data-sentry-source-file="RealEstateDetail.tsx" />;
    $[151] = realEstateId;
    $[152] = t41;
    $[153] = t42;
    $[154] = t43;
  } else {
    t43 = $[154];
  }
  let t44;
  if ($[155] !== detailData.businessType || $[156] !== detailData.dependableUsableArea || $[157] !== detailData.plotArea || $[158] !== detailData.privateUsableArea || $[159] !== detailData.salePrice || $[160] !== detailData.terraceArea || $[161] !== detailData.totalArea || $[162] !== detailData.usableArea) {
    t44 = detailData?.businessType !== BusinessType.RoomRent && <Card className="bg-card border-none shadow-sm"><CardContent><AreaContent totalArea={detailData?.totalArea} usableArea={detailData.usableArea} dependableUsableArea={detailData.dependableUsableArea} privateUsableArea={detailData.privateUsableArea} plotArea={detailData.plotArea} terraceArea={detailData.terraceArea} salePrice={Number(detailData.salePrice)} /></CardContent></Card>;
    $[155] = detailData.businessType;
    $[156] = detailData.dependableUsableArea;
    $[157] = detailData.plotArea;
    $[158] = detailData.privateUsableArea;
    $[159] = detailData.salePrice;
    $[160] = detailData.terraceArea;
    $[161] = detailData.totalArea;
    $[162] = detailData.usableArea;
    $[163] = t44;
  } else {
    t44 = $[163];
  }
  let t45;
  if ($[164] !== detailData.roomData || $[165] !== isDemand) {
    t45 = detailData?.roomData && <RealEstateDetailHouseRules petFriendly={detailData.roomData.petFriendly} contract={detailData.roomData.contract} smokingAllowed={detailData.roomData.smokingAllowed} isDemand={isDemand} />;
    $[164] = detailData.roomData;
    $[165] = isDemand;
    $[166] = t45;
  } else {
    t45 = $[166];
  }
  let t46;
  if ($[167] !== detailData.roomData?.bills || $[168] !== isDemand) {
    t46 = !isDemand && <RealEstateDetailBills bills={detailData?.roomData?.bills} />;
    $[167] = detailData.roomData?.bills;
    $[168] = isDemand;
    $[169] = t46;
  } else {
    t46 = $[169];
  }
  const t47 = detailData.slug;
  const t48 = detailData.businessType;
  const t49 = detailData.listingType;
  const t50 = detailData.totalArea;
  let t51;
  if ($[170] !== detailData.businessType || $[171] !== detailData.lastBidPrice || $[172] !== detailData.minPrice || $[173] !== detailData.salePrice) {
    t51 = getPriceDisplay({
      businessType: detailData.businessType,
      salePrice: detailData.salePrice,
      lastBidPrice: detailData.lastBidPrice,
      minPrice: detailData.minPrice
    });
    $[170] = detailData.businessType;
    $[171] = detailData.lastBidPrice;
    $[172] = detailData.minPrice;
    $[173] = detailData.salePrice;
    $[174] = t51;
  } else {
    t51 = $[174];
  }
  let t52;
  if ($[175] !== detailData.businessType || $[176] !== detailData.listingType || $[177] !== detailData.slug || $[178] !== detailData.totalArea || $[179] !== t51.value) {
    t52 = <Compare slug={t47} originalBusinessType={t48} originalListingType={t49} originalArea={t50} originalPrice={t51.value} data-sentry-element="Compare" data-sentry-source-file="RealEstateDetail.tsx" />;
    $[175] = detailData.businessType;
    $[176] = detailData.listingType;
    $[177] = detailData.slug;
    $[178] = detailData.totalArea;
    $[179] = t51.value;
    $[180] = t52;
  } else {
    t52 = $[180];
  }
  let t53;
  if ($[181] !== detailData.updatedAt || $[182] !== inDialog || $[183] !== locale || $[184] !== tRealEstateDetail) {
    t53 = !inDialog && detailData?.updatedAt && <p className="text-[11px] text-faint-foreground text-center">{tRealEstateDetail("updatedAgo", {
        time: formatTimeAgo(detailData.updatedAt, locale === "pt" ? "pt" : "enGB")
      })}</p>;
    $[181] = detailData.updatedAt;
    $[182] = inDialog;
    $[183] = locale;
    $[184] = tRealEstateDetail;
    $[185] = t53;
  } else {
    t53 = $[185];
  }
  let t54;
  if ($[186] !== t34 || $[187] !== t40 || $[188] !== t43 || $[189] !== t44 || $[190] !== t45 || $[191] !== t46 || $[192] !== t52 || $[193] !== t53) {
    t54 = <div className="lg:col-span-1 2xl:col-span-1 relative"><div className={t34}>{t40}{t43}{t44}{t45}{t46}{t52}{t53}</div></div>;
    $[186] = t34;
    $[187] = t40;
    $[188] = t43;
    $[189] = t44;
    $[190] = t45;
    $[191] = t46;
    $[192] = t52;
    $[193] = t53;
    $[194] = t54;
  } else {
    t54 = $[194];
  }
  let t55;
  if ($[195] !== t19 || $[196] !== t32 || $[197] !== t54) {
    t55 = <div className="pb-44 " data-sentry-component="RealEstateDialogContent" data-sentry-source-file="RealEstateDetail.tsx"><div className={t19}>{t32}{t54}</div></div>;
    $[195] = t19;
    $[196] = t32;
    $[197] = t54;
    $[198] = t55;
  } else {
    t55 = $[198];
  }
  return t55;
}
function _temp8(count) {
  return `+${count}`;
}
function _temp7(points) {
  return points.length > 0;
}
function _temp6(geometry) {
  return convertPolygon(geometry);
}
function _temp5(feature_1) {
  return feature_1.featureKey === "bathrooms";
}
function _temp4(feature_0) {
  return feature_0.featureKey === "bedrooms";
}
function _temp3(feature) {
  return feature.featureKey === "real_estate_type";
}
function _temp2(a, b) {
  return a.title.localeCompare(b.title) || a.slug.localeCompare(b.slug);
}
function _temp(r) {
  return {
    slug: r.slug,
    title: r.title,
    rentPrice: r.rentPrice ?? null,
    thumbnail: r.thumbnail ?? null,
    matchesFilters: r.matchesFilters,
    gender: r.gender ?? null,
    availableFrom: r.availableFrom ?? null,
    petFriendly: r.petFriendly ?? null,
    contract: r.contract ?? null,
    smokingAllowed: r.smokingAllowed ?? null
  };
}